import type { CaseStage, DealerStatus } from '@tex/finance-shared'
import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react'
import { STAGE_LABELS } from '../lib/format'

const button = {
  primary:
    'border-tex-blue bg-tex-blue text-white shadow-sm hover:border-[var(--Classic-Blue-hover)] hover:bg-[var(--Classic-Blue-hover)]',
  secondary: 'border-[var(--border-strong)] bg-white text-[var(--text-strong)] shadow-sm hover:bg-neutral-50',
  ghost: 'border-transparent bg-transparent text-tex-blue hover:bg-[var(--surface-muted)]',
  danger: 'border-tex-red bg-white text-tex-red shadow-sm hover:bg-red-50',
} as const

export function Button({
  variant = 'primary',
  className = '',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: keyof typeof button }) {
  return (
    <button
      {...props}
      className={`inline-flex items-center justify-center gap-1.5 rounded-md border px-3 py-1.5 font-display text-xs font-bold uppercase tracking-[0.14em] transition-all duration-150 disabled:cursor-not-allowed disabled:opacity-50 ${button[variant]} ${className}`}
    />
  )
}

const control =
  'w-full rounded-lg border border-[var(--border-outline)] bg-white px-3 py-2 font-sans text-sm text-[var(--text-body)] shadow-sm transition-colors placeholder:text-[var(--text-muted)]/60 focus:border-tex-blue focus:outline-none disabled:bg-neutral-50 disabled:text-neutral-500 read-only:bg-neutral-50'

export function Input(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={`${control} ${props.className ?? ''}`} />
}

export function Select(props: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...props} className={`${control} cursor-pointer ${props.className ?? ''}`} />
}

export function Textarea(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...props} className={`${control} ${props.className ?? ''}`} />
}

export function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="block text-sm">
      <span className="mb-1 block font-display text-xs font-bold uppercase tracking-wide text-[var(--text-strong)]">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-xs text-[var(--text-muted)]">{hint}</span>}
    </label>
  )
}

export function Alert({ tone, children }: { tone: 'error' | 'info' | 'success'; children: ReactNode }) {
  const tones = {
    error: 'border-red-200 bg-red-50 text-red-800',
    info: 'border-[var(--border-outline)] bg-blue-50/40 text-neutral-800',
    success: 'border-green-200 bg-green-50 text-green-800',
  }
  return (
    <div role={tone === 'error' ? 'alert' : 'status'} className={`rounded-lg border px-3 py-2.5 text-sm ${tones[tone]}`}>
      {children}
    </div>
  )
}

export function Card({ title, actions, children }: { title: string; actions?: ReactNode; children: ReactNode }) {
  return (
    <section className="rounded-xl border border-[var(--border-subtle)] bg-white shadow-sm">
      <header className="flex items-center justify-between gap-3 border-b border-neutral-100 px-4 py-2.5">
        <h2 className="font-display text-sm font-bold uppercase tracking-[0.04em] text-tex-blue">{title}</h2>
        {actions}
      </header>
      <div className="p-4">{children}</div>
    </section>
  )
}

/** Page title block: Cairo display heading with optional lead and right-aligned actions. */
export function PageHeader({
  title,
  description,
  actions,
  eyebrow,
}: {
  title: ReactNode
  description?: ReactNode
  actions?: ReactNode
  eyebrow?: ReactNode
}) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4">
      <div className="min-w-0">
        {eyebrow && <div className="tex-eyebrow mb-1">{eyebrow}</div>}
        <h1 className="font-display text-3xl font-bold text-neutral-900">{title}</h1>
        {description && <p className="mt-1 text-sm text-neutral-500">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-end gap-2">{actions}</div>}
    </div>
  )
}

const badge =
  'inline-flex items-center gap-1 whitespace-nowrap rounded-md border px-1.5 py-0.5 font-mono text-[11px] font-semibold uppercase tracking-wide'

const statusTone: Record<DealerStatus, string> = {
  pending_review: 'border-amber-200 bg-amber-50 text-amber-800',
  active: 'border-green-200 bg-green-50 text-[var(--status-success)]',
  suspended: 'border-red-200 bg-red-50 text-[var(--status-danger)]',
}

export function StatusBadge({ status }: { status: DealerStatus }) {
  return <span className={`${badge} ${statusTone[status]}`}>{status.replace('_', ' ')}</span>
}

const stageTone: Record<CaseStage, string> = {
  lead: 'border-neutral-200 bg-neutral-100 text-neutral-800',
  application: 'border-primary-200 bg-primary-50 text-tex-blue',
  review: 'border-amber-200 bg-amber-50 text-amber-800',
  lender_review: 'border-amber-200 bg-amber-50 text-amber-800',
  offer_review: 'border-primary-200 bg-primary-100 text-tex-blue',
  closing: 'border-primary-200 bg-primary-100 text-tex-blue',
  funded: 'border-green-200 bg-green-50 text-[var(--status-success)]',
  closed: 'border-neutral-300 bg-neutral-200 text-neutral-700',
}

export function StageBadge({ stage }: { stage: CaseStage }) {
  return <span className={`${badge} ${stageTone[stage]}`}>{STAGE_LABELS[stage]}</span>
}

/** Identifiers, references and amounts render in the mono face. */
export function Mono({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <span className={`font-mono tabular-nums ${className}`}>{children}</span>
}

export function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex justify-between gap-4 py-1 text-sm">
      <dt className="text-neutral-500">{label}</dt>
      <dd className="text-right font-medium text-neutral-900">
        {value === null || value === undefined || value === '' ? '—' : value}
      </dd>
    </div>
  )
}
